/*
 * @author Andrey Patyuchenko
 * @email  apatyuchenko@atlassian.com
 * @email  andrey.patyuchenko@gmail.com
 * @date   27/03/2023
 */

import React, { useState } from "react";
import {
	Alert,
	Badge,
	Box,
	Button,
	Fade,
	FormControlLabel,
	FormGroup,
	IconButton,
	Paper,
	Popover,
	Stack,
	Switch,
	Tooltip,
	Typography
} from "@mui/material";
import NotificationsIcon from "@mui/icons-material/Notifications";
import NotificationsNoneIcon from "@mui/icons-material/NotificationsNone";
import CheckIcon from "@mui/icons-material/Check";
import MarkChatReadIcon from "@mui/icons-material/MarkChatRead";
import { useNotificationCenter } from "react-toastify/addons/use-notification-center";
import { formatDistanceToNowStrict } from "date-fns";

export default function NotificationCenter() {

	const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
	const [ showUnreadOnly, setShowUnreadOnly ] = React.useState(false);
	const isOpen = Boolean(anchorEl);

	const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
		setAnchorEl(event.currentTarget);
	};

	const handleClose = () => {
		setAnchorEl(null);
	};

	const { notifications, unreadCount, markAllAsRead, clear, markAsRead } = useNotificationCenter();

	return (
		<Box>
			<Tooltip title={"Notifications"} placement={"right"}>
				<IconButton onClick={handleClick} sx={{ color: "#fff", fontSize: 26 }}>
					<Badge
						color={"error"}
						anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
						badgeContent={notifications.length ?? undefined}
					>
						{!!unreadCount && <NotificationsIcon />}
						{!unreadCount && <NotificationsNoneIcon />}
					</Badge>
				</IconButton>
			</Tooltip>
			<Popover
				open={isOpen}
				anchorEl={anchorEl}
				onClose={handleClose}
				TransitionComponent={Fade}
				anchorOrigin={{
					vertical: 'center',
					horizontal: 'right',
				}}
				transformOrigin={{
					vertical: 'bottom',
					horizontal: 'left',
				}}
			>
				<Stack spacing={2} p={2}>
					<Stack direction={"row"} justifyContent={"space-between"} alignItems={"center"}>
						<Typography variant={"h6"}>
							Notifications
						</Typography>
						<FormGroup>
							<FormControlLabel
								control={<Switch onClick={() => setShowUnreadOnly(!showUnreadOnly)} />}
								label="Unread"
								labelPlacement={"start"}
							/>
						</FormGroup>
					</Stack>
					<Stack
						sx={{
							height: 400,
							width: "min(60ch, 100ch)",
							overflowY: "auto"
						}}
						spacing={1}
					>
						{(!notifications.length ||
							(unreadCount === 0 && showUnreadOnly)) && (
							<Paper
								variant={"outlined"}
								sx={{
									py: 4,
									px: 2,
									textAlign: "center"
								}}
							>
								No notifications.
							</Paper>
						)}
						{(showUnreadOnly
								? notifications.filter((v) => !v.read)
								: notifications
						).map((n) =>
							<Alert
								key={n.id}
								severity={n.type === "default" ? "info" : n.type}
								action={
									n.read ? <CheckIcon /> : (
										<Tooltip title={"Mark as read"}>
											<IconButton
												size={"small"}
												color="primary"
												component="span"
												onClick={() => markAsRead(n.id)}
											>
												<MarkChatReadIcon />
											</IconButton>
										</Tooltip>
									)
								}
							>
								<Stack>
									<Stack>
										{n.content}
									</Stack>
									<Typography variant={"body2"} textAlign={"right"} fontSize={"0.9em"}>
										{formatDistanceToNowStrict(n.createdAt, { addSuffix: true })}
									</Typography>
								</Stack>
							</Alert>)}
					</Stack>
					<Stack direction={"row"} justifyContent={"space-between"}>
						<Button
							size={"small"}
							onClick={() => clear()}
						>
							Clear All
						</Button>
						<Button
							size={"small"}
							onClick={() => markAllAsRead()}
						>
							Mark All As Read
						</Button>
					</Stack>
				</Stack>
			</Popover>
		</Box>
	);

}